import { type ButtonHTMLAttributes, type ReactNode, forwardRef } from 'react';
import { Loader2 } from 'lucide-react';
import type { Label as LabelType, User } from '@shared/types';
import { avatarColour, cn, contrastText, initials } from '@/lib/format';

/* ---------- Button ---------- */

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
type ButtonSize = 'sm' | 'md';

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700 disabled:hover:bg-brand-500',
  secondary:
    'surface-card text-body border line hover:surface-hover hover:text-strong disabled:hover:bg-[var(--surface-card)]',
  ghost: 'text-muted hover:text-strong hover:bg-[var(--surface-hover)]',
  danger: 'bg-red-600 text-white hover:bg-red-700 active:bg-red-800',
};

const BUTTON_SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 px-2.5 text-[13px] gap-1.5',
  md: 'h-9 px-3.5 text-sm gap-2',
};

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', loading = false, disabled, className, children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      // A loading button must not be clickable twice.
      disabled={disabled || loading}
      className={cn(
        'inline-flex items-center justify-center rounded-lg font-medium transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-55',
        BUTTON_VARIANTS[variant],
        BUTTON_SIZES[size],
        className,
      )}
      {...rest}
    >
      {loading && <Loader2 className="size-4 animate-spin" aria-hidden />}
      {children}
    </button>
  );
});

/* ---------- Avatar ---------- */

export function Avatar({
  user,
  size = 'md',
}: {
  user: User;
  size?: 'xs' | 'sm' | 'md';
}) {
  const dimensions = { xs: 'size-5 text-[9px]', sm: 'size-6 text-[10px]', md: 'size-8 text-xs' };
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full font-semibold text-white uppercase',
        'ring-1 ring-black/5',
        avatarColour(user.id),
        dimensions[size],
      )}
      // The visible initials are ambiguous, so the full name goes in the title
      // and the accessible label.
      title={user.name}
      aria-label={user.name}
    >
      {initials(user.name)}
    </span>
  );
}

export function AvatarGroup({ users, max = 3 }: { users: User[]; max?: number }) {
  if (users.length === 0) return null;
  const shown = users.slice(0, max);
  const overflow = users.length - shown.length;
  return (
    <span className="flex items-center -space-x-1.5">
      {shown.map((u) => (
        <span key={u.id} className="ring-2 ring-[var(--surface-card)] rounded-full">
          <Avatar user={u} size="sm" />
        </span>
      ))}
      {overflow > 0 && (
        <span
          className="inline-flex size-6 items-center justify-center rounded-full surface-sunken
                     text-[10px] font-semibold text-muted ring-2 ring-[var(--surface-card)]"
          title={users.slice(max).map((u) => u.name).join(', ')}
        >
          +{overflow}
        </span>
      )}
    </span>
  );
}

/* ---------- Label chip ---------- */

export function LabelChip({ label, size = 'md' }: { label: LabelType; size?: 'sm' | 'md' }) {
  const bg = label.hexColor ?? '#64748b';
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full font-medium whitespace-nowrap',
        size === 'sm' ? 'px-1.5 py-px text-[10px]' : 'px-2 py-0.5 text-[11px]',
      )}
      // Colours come from the database, so they can't be Tailwind classes.
      // contrastText keeps the text readable on whatever colour was chosen.
      style={{ backgroundColor: bg, color: contrastText(bg) }}
    >
      {label.title}
    </span>
  );
}

/* ---------- states ---------- */

export function Spinner({ className }: { className?: string }) {
  return (
    <Loader2
      className={cn('size-5 animate-spin text-muted', className)}
      role="status"
      aria-label="Lädt"
    />
  );
}

export function CenteredSpinner({ label = 'Lädt…' }: { label?: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-16">
      <Spinner />
      <p className="text-sm text-muted">{label}</p>
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  // Fills the parent (via `h-full min-h-[24rem]`) so the icon/text sit in the
  // middle of the available area instead of clinging to the top of a tall
  // scroll container — MyTasksPage / InboxPage empty states used to appear
  // ~120px from the top of an ~800px viewport, leaving a huge blank band.
  return (
    <div className="flex h-full min-h-[24rem] flex-col items-center justify-center gap-3 px-6 py-16 text-center">
      {icon && <div className="text-faint [&>svg]:size-8" aria-hidden>{icon}</div>}
      <h3 className="text-sm font-semibold text-strong">{title}</h3>
      {description && <p className="max-w-sm text-sm text-muted">{description}</p>}
      {action}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const message = error instanceof Error ? error.message : 'Unbekannter Fehler';
  return (
    <div
      className="m-4 rounded-[var(--radius-card)] border border-red-200 bg-red-50 p-4
                 dark:border-red-900/50 dark:bg-red-950/30"
      role="alert"
    >
      <p className="text-sm font-semibold text-red-800 dark:text-red-300">
        Etwas ist schiefgelaufen
      </p>
      <p className="mt-1 text-sm text-red-700 dark:text-red-400">{message}</p>
      {onRetry && (
        <Button size="sm" variant="secondary" className="mt-3" onClick={onRetry}>
          Erneut versuchen
        </Button>
      )}
    </div>
  );
}

/** Skeleton row for list loading states — steadier than a spinner per row. */
export function SkeletonRows({ count = 8 }: { count?: number }) {
  return (
    <div className="divide-y line" aria-hidden>
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="flex items-center gap-3 px-4 py-3">
          <div className="size-4 shrink-0 rounded surface-sunken animate-pulse" />
          <div
            className="h-3.5 rounded surface-sunken animate-pulse"
            // Varied widths so it reads as text rather than a progress bar.
            style={{ width: `${35 + ((i * 17) % 45)}%` }}
          />
        </div>
      ))}
    </div>
  );
}
